import React, { useMemo } from "react";
import { getCampaignCategoryOptions } from "../utils/constants";
import CheckIcon from "../../../assets/svg/check-icon";
import Button from "../../Button/Button";
import { useState } from "react";
import { useGetAllCampaignCategoriesQuery } from "../../../redux/services/campaignApi";
import { useDispatch, useSelector } from "react-redux";
import {
  clearCampaignsCategory,
  clearCampaignTemplatesCategory,
  selectCampaignsFilters,
  selectCampaignTemplatesFilters,
  setCampaignsBranch,
  setCampaignsCategory,
  setCampaignsUseCase,
  setCampaignTemplatesCategory,
} from "../../../redux/slices/filterSlice";
import { setCurrentPage } from "../../../redux/slices/allCampaignSlice";
import { WORKFLO_NAME } from "../../../utils/constants/globalConstants";
import { RootState } from "../../../redux/store";

interface CategoryType {
  key: string;
  display_name: string;
  description?: string;
}

interface ProcessedCategory extends CategoryType {
  value: string;
  content?: React.ReactNode;
}

interface CampaignCategoryFilterProps {
  onClose: () => void;
  campaignTemplates?: boolean;
}

type CategoryKey = keyof ReturnType<typeof getCampaignCategoryOptions>;

const CampaignCategoryFilter: React.FC<CampaignCategoryFilterProps> = ({
  onClose,
  campaignTemplates = false,
}) => {
  const campaignFilters = useSelector(
    campaignTemplates ? selectCampaignTemplatesFilters : selectCampaignsFilters
  );
  const clientCode = useSelector(
    (state: RootState) => state?.auth?.client?.client_code
  );
  const campaignCategoryOptions = useMemo(
    () => getCampaignCategoryOptions(clientCode),
    [clientCode]
  );
  const [selectedOption, setSelectedOption] = useState<string>(
    campaignFilters.category.value as string
  );
  const dispatch = useDispatch();

  const {
    data: { categories: rawCategories = [] } = { categories: [] },
    isLoading: isCategoriesLoading,
  } = useGetAllCampaignCategoriesQuery({});

  // Combine API data with our UI components
  const categories = rawCategories.map((category: CategoryType) => {
    const key = category.key.toLowerCase();
    const uiData = campaignCategoryOptions[key as CategoryKey];

    return {
      ...category,
      value: key,
      content: uiData?.content,
    };
  });

  const handleApply = () => {
    if (selectedOption === campaignFilters?.category?.value) {
      return;
    }
    dispatch(
      campaignTemplates
        ? setCampaignTemplatesCategory({
            value: selectedOption,
            displayValue:
              campaignCategoryOptions[selectedOption as CategoryKey]
                .displayValue,
          })
        : setCampaignsCategory({
            value: selectedOption,
            displayValue:
              campaignCategoryOptions[selectedOption as CategoryKey]
                .displayValue,
          })
    );
    dispatch(setCampaignsUseCase({ value: "", displayValue: "" }));
    dispatch(setCampaignsBranch({ value: "", displayValue: "" }));
    dispatch(setCurrentPage(1));
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="flex flex-col gap-2 mt-2 px-3">
      <h2 className="text-md text-heading-text dark:text-heading-text-dk py-1.5">
        Select {WORKFLO_NAME.singular} category filter
      </h2>
      {isCategoriesLoading ? (
        <div className="flex items-center justify-center text-md text-heading-text dark:text-heading-text-dk">
          Loading categories...
        </div>
      ) : (
        <>
          {categories.map((category: ProcessedCategory) => (
            <div
              key={category.key}
              onClick={() => setSelectedOption(category.value)}
              className={`flex items-center justify-between border cursor-pointer ${
                selectedOption === category.value
                  ? "border-verba-blue dark:border-verba-blue-dk bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk"
                  : "border-border-gray dark:border-border-gray-dk"
              } rounded-lg py-[13.5px] px-3`}
            >
              <div>{category.content || category.display_name}</div>
              {selectedOption === category.value && (
                <CheckIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
              )}
            </div>
          ))}
        </>
      )}
      <div className="flex justify-end gap-2 mt-4">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={handleCancel}
        />
        <Button
          innerText="Apply"
          disabled={selectedOption === campaignFilters?.category?.value}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default CampaignCategoryFilter;
